import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ADMIN_ROLES, PASSWORD_MIN_LENGTH, type Paginated } from '@insurensa/shared';
import { Badge, Card, PageTitle } from '@insurensa/ui';
import { useFormat, useT } from '@insurensa/i18n';
import { api, ApiError } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { BlockSkeleton, ErrorAlert } from '../components/ui.js';

interface AdminRow {
  id: string;
  name: string;
  email: string;
  username: string;
  role: string;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

/**
 * Replaces addUser.php / editUser.php / insertUser.php / updateUser.php.
 *
 * The role column is now actually enforced by the API, so these are real
 * permissions rather than a label the legacy console never read.
 */
export function TeamPage(): JSX.Element {
  const t = useT();
  const fmt = useFormat();
  const queryClient = useQueryClient();
  const { admin } = useAuth();
  const [form, setForm] = useState({
    name: '',
    email: '',
    username: '',
    role: 'ADMIN',
    password: '',
  });

  const { data, error, isLoading } = useQuery({
    queryKey: ['admins'],
    queryFn: () => api.get<Paginated<AdminRow>>('/admin/admins?page=1&pageSize=50'),
  });

  const create = useMutation({
    mutationFn: (input: typeof form) => api.post('/admin/admins', input),
    onSuccess: () => {
      setForm({ name: '', email: '', username: '', role: 'ADMIN', password: '' });
      void queryClient.invalidateQueries({ queryKey: ['admins'] });
    },
  });

  const update = useMutation({
    mutationFn: (input: { id: string; patch: Record<string, unknown> }) =>
      api.patch(`/admin/admins/${input.id}`, input.patch),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['admins'] }),
  });

  const createError = create.error instanceof ApiError ? create.error : null;

  const onSubmit = (event: FormEvent): void => {
    event.preventDefault();
    create.mutate(form);
  };

  return (
    <>
      <PageTitle
        heading={t('admin.nav.team')}
        subheading={t('admin.team.subheading')}
        icon="pe-7s-id"
      />

      <ErrorAlert error={error} />
      <ErrorAlert error={create.error} />
      <ErrorAlert error={update.error} />

      <div className="row">
        <div className="col-lg-8">
          <Card title={<div className="card-header-title">{t('admin.team.members')}</div>} noPadding>
            {isLoading ? (
              <div className="p-3">
                <BlockSkeleton height={90} />
              </div>
            ) : (
              <table className="table table-hover table-striped mb-0">
                <thead>
                  <tr>
                    <th>{t('fields.name')}</th>
                    <th>{t('fields.username')}</th>
                    <th>{t('fields.role')}</th>
                    <th>{t('common.status')}</th>
                    <th>{t('fields.lastSignIn')}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {data?.items.map((row) => (
                    <tr key={row.id}>
                      <td>
                        <strong>{row.name}</strong>
                        <div className="small text-muted">{row.email}</div>
                      </td>
                      <td>{row.username}</td>
                      <td>
                        <Badge tone={row.role === 'SUPER_ADMIN' ? 'primary' : 'secondary'}>
                          {fmt.role(row.role)}
                        </Badge>
                      </td>
                      <td>
                        <Badge tone={row.isActive ? 'success' : 'danger'}>
                          {row.isActive ? t('common.active') : t('admin.team.disabled')}
                        </Badge>
                      </td>
                      <td>{fmt.dateTime(row.lastLoginAt)}</td>
                      <td>
                        {row.id === admin?.id ? (
                          <span className="small text-muted">{t('admin.team.yourself')}</span>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-outline-secondary btn-sm"
                            disabled={update.isPending}
                            onClick={() =>
                              update.mutate({ id: row.id, patch: { isActive: !row.isActive } })
                            }
                          >
                            {row.isActive ? t('common.deactivate') : t('common.activate')}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </div>

        <div className="col-lg-4">
          <Card title={<div className="card-header-title">{t('admin.team.createAccess')}</div>}>
            <form onSubmit={onSubmit}>
              <div className="position-relative form-group">
                <label htmlFor="name">{t('fields.name')}</label>
                <input
                  id="name"
                  type="text"
                  className="form-control"
                  required
                  value={form.name}
                  onChange={(event) => setForm({ ...form, name: event.target.value })}
                />
              </div>

              <div className="position-relative form-group">
                <label htmlFor="email">{t('fields.email')}</label>
                <input
                  id="email"
                  type="email"
                  className="form-control"
                  required
                  value={form.email}
                  onChange={(event) => setForm({ ...form, email: event.target.value })}
                  aria-invalid={createError?.fieldError('email') ? 'true' : undefined}
                />
                {createError?.fieldError('email') && (
                  <div className="field-error">{createError.fieldError('email')}</div>
                )}
              </div>

              <div className="position-relative form-group">
                <label htmlFor="username">{t('fields.username')}</label>
                <input
                  id="username"
                  type="text"
                  className="form-control"
                  required
                  value={form.username}
                  onChange={(event) => setForm({ ...form, username: event.target.value })}
                  aria-invalid={createError?.fieldError('username') ? 'true' : undefined}
                />
                {createError?.fieldError('username') && (
                  <div className="field-error">{createError.fieldError('username')}</div>
                )}
              </div>

              <div className="position-relative form-group">
                <label htmlFor="role">{t('fields.role')}</label>
                <select
                  id="role"
                  className="form-control"
                  value={form.role}
                  onChange={(event) => setForm({ ...form, role: event.target.value })}
                >
                  {ADMIN_ROLES.map((role) => (
                    <option key={role} value={role}>
                      {fmt.role(role)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="position-relative form-group">
                <label htmlFor="password">{t('admin.team.initialPassword')}</label>
                <input
                  id="password"
                  type="password"
                  className="form-control"
                  required
                  minLength={PASSWORD_MIN_LENGTH}
                  value={form.password}
                  onChange={(event) => setForm({ ...form, password: event.target.value })}
                  aria-invalid={createError?.fieldError('password') ? 'true' : undefined}
                />
                <small className="form-text text-muted">
                  {t('admin.team.initialPasswordHint', { min: PASSWORD_MIN_LENGTH })}
                </small>
              </div>

              <button type="submit" className="btn btn-primary" disabled={create.isPending}>
                {create.isPending ? t('admin.team.creating') : t('admin.team.createAccess')}
              </button>
            </form>
          </Card>
        </div>
      </div>
    </>
  );
}
